import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { getMiniCRMSchools, updateSchool } from "../../../api/miniCrm.service";
import constant from "../../../utils/constant";
import { useNavigate } from "react-router";
import Pagination from "../../shared/common/pagination";
import SelectPageSize from "../../shared/common/pageSizeDropdown";
import moment from "moment";
import { CaretDownSolidIcon, CaretUpSolidIcon } from "../../../assets/icons";
import { DistrictSchoolInlineEditCell } from "./DistrictSchoolInlineEditCell";
import { toast } from "react-toastify";

export interface IDistrictSchoolListHandle {
  refresh: () => void;
}

interface DistrictSchoolListProps {
  districtId: number;
  hasCleverId: boolean;
  hasClasslinkId: boolean;
  userId?: number;
  setLoading: (loading: boolean) => void;
  ref?: React.Ref<IDistrictSchoolListHandle>;
}

export const DistrictSchoolList = (props: DistrictSchoolListProps) => {
  const { districtId, hasCleverId, hasClasslinkId, userId, setLoading, ref } =
    props;

  const navigate = useNavigate();
  const paginationRef: any = useRef(undefined);
  const pageSizeRef: any = useRef(undefined);
  const [schools, setSchools] = useState<Array<any>>([]);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const [sortCol, setSortCol] = useState<string>("SchoolName");
  const [sortByDesc, setSortByDesc] = useState<boolean>(false);

  const [editingSchoolId, setEditingSchoolId] = useState<number | null>(null);

  const searchSchools = async () => {
    const params = {
      stateId: 0,
      region: "",
      districtId: districtId,
      districtName: "",
      schoolName: "",
      city: "",
      orderByColumn: sortCol,
      sortDir: sortByDesc ? "desc" : "asc", // Updated to use dynamic sort direction
      pageSize: pageSize,
      pageNumber: pageNumber,
    };

    setLoading(true);
    try {
      const resp = await getMiniCRMSchools(params);

      if (resp && resp.data && resp.data.data) {
        setEditingSchoolId(null);
        setSchools(resp.data.data);
        setTotalPages(Math.ceil(resp.data.totalRecords / pageSize));
        setTotalRecords(resp.data.totalRecords);
      }
    } finally {
      setLoading(false);
    }
  };

  const openSchool = (schoolId) => {
    navigate(constant.SCHOOLDETAIL.replace(":id", schoolId));
  };

  const getRosterId = (school): string =>
    school.cleverId || school.classLinkId || "";

  const saveEdit = async (schoolId: number, value: string) => {
    const school = schools.find((item) => item.schoolId === schoolId);
    const newValue = value.trim();

    if (newValue === "") {
      toast.error("Value cannot be blank");
      setEditingSchoolId(null);
      return;
    }

    const newSchoolData = {
      name: school.name,
      schoolId: school.schoolId,
      stateName: school.stateName,
      stateId: school.stateId,
      districtName: school.districtName,
      districtId: school.districtId,
      classlinkId: school.classLinkId ?? null,
      isClassLinkActive: school.isClassLinkActive,
      cleverId: school.cleverId ?? null,
      isCleverActive: school.isCleverActive,
      city: school.city,
      zip: school.zip,
      updatedByUser: userId,
      isNWEAEnabled: school.isNWEAEnabled,
      hasEnhancedPasswordSecurity: school.hasEnhancedPasswordSecurity,
    };

    if (school.isCleverActive) {
      newSchoolData.cleverId = newValue;
    }

    if (school.isClassLinkActive) {
      newSchoolData.classlinkId = newValue;
    }

    if (!school.isCleverActive && !school.isClassLinkActive) {
      if (hasCleverId) {
        newSchoolData.cleverId = newValue;
        newSchoolData.isCleverActive = true;
      }

      if (!hasCleverId) {
        newSchoolData.classlinkId = newValue;
        newSchoolData.isClassLinkActive = true;
      }
    }

    setLoading(true);
    try {
      const resp = await updateSchool(newSchoolData);
      if (resp.status === 200) {
        toast.success("Update Successful", { autoClose: 1200 });
        await searchSchools();
      } else {
        toast.error("Update failed");
      }
    } catch (e) {
      toast.error("Update failed");
    } finally {
      setEditingSchoolId(null);
      setLoading(false);
    }
  };

  const cancelEdit = useCallback(() => {
    setEditingSchoolId(null);
  }, []);

  // Handle sorting
  const handleSortBy = (column: string) => {
    if (sortCol === column) {
      setSortByDesc(!sortByDesc);
    } else {
      setSortByDesc(false);
      setSortCol(column);
    }
  };

  useEffect(() => {
    searchSchools();
  }, [districtId, sortCol, sortByDesc, pageNumber, pageSize]);

  useImperativeHandle(ref, () => ({
    refresh() {
      searchSchools();
    },
  }));

  const getProviderTableHeader = () => {
    if (hasCleverId) {
      return "Clever ID";
    }

    if (hasClasslinkId) {
      return "ClassLink ID";
    }

    return "Clever/ClassLink ID";
  };

  return (
    <>
      <div id="dataContainer" className="mt-4 border-t pt-6">
        <div className="flex flex-col mt-1">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-primary-color">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        <span className="flex">Name</span>
                      </th>
                      {/* New columns */}
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        <span className="flex gap-2">
                          {getProviderTableHeader()}
                          <span className="cursor-pointer">
                            {sortByDesc && sortCol === "CleverClassLinkID" ? (
                              <CaretUpSolidIcon
                                onClick={() =>
                                  handleSortBy("CleverClassLinkID")
                                }
                                className="w-5 h-5"
                              ></CaretUpSolidIcon>
                            ) : (
                              <CaretDownSolidIcon
                                onClick={() =>
                                  handleSortBy("CleverClassLinkID")
                                }
                                className="w-5 h-5"
                              ></CaretDownSolidIcon>
                            )}
                          </span>
                        </span>
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 font-light text-sm text-white dark:text-white tracking-wider"
                      >
                        <span className="flex gap-2">
                          Latest Subscription End Date
                          <span className="cursor-pointer">
                            {sortByDesc && sortCol === "SubscriptionEndDate" ? (
                              <CaretUpSolidIcon
                                onClick={() =>
                                  handleSortBy("SubscriptionEndDate")
                                }
                                className="w-5 h-5"
                              ></CaretUpSolidIcon>
                            ) : (
                              <CaretDownSolidIcon
                                onClick={() =>
                                  handleSortBy("SubscriptionEndDate")
                                }
                                className="w-5 h-5"
                              ></CaretDownSolidIcon>
                            )}
                          </span>
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {schools.map((school, i) => (
                      <tr
                        key={i}
                        className={`align-top ${
                          i % 2 === 0 ? "bg-white " : "bg-gray-100"
                        }`}
                      >
                        <td className=" px-3 py-2  text-sm text-left font-normal relative">
                          <button
                            className="text-indigo-500"
                            type="button"
                            onClick={() => {
                              openSchool(school.schoolId);
                            }}
                          >
                            {school.name}
                          </button>
                        </td>
                        {hasCleverId || hasClasslinkId ? (
                          <DistrictSchoolInlineEditCell
                            schoolId={school.schoolId}
                            value={getRosterId(school)}
                            isEditing={editingSchoolId === school.schoolId}
                            onStartEdit={setEditingSchoolId}
                            onSave={saveEdit}
                            onCancel={cancelEdit}
                          />
                        ) : (
                          <td className="px-3 py-2 text-sm text-left font-normal">
                            {"N/A"}
                          </td>
                        )}
                        <td className="px-3 py-2 text-sm text-left font-normal">
                          {school.latestSubscriptionEndDate
                            ? moment(school.latestSubscriptionEndDate).format(
                                "MM-DD-yyyy"
                              )
                            : "N/A"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {schools.length === 0 && (
                  <p className="p-2">No Records Found</p>
                )}
              </div>
            </div>
          </div>
          <div className="flex justify-between items-center">
            <Pagination
              pages={totalPages}
              setPageNumber={setPageNumber}
              ref={paginationRef}
            />
            <span>
              <span className="text-sm pr-4 ml-auto text-left-700">
                Showing {pageNumber} to{" "}
                {totalRecords < pageSize ? totalRecords : pageSize} of{" "}
                {totalRecords} results{" "}
              </span>

              <SelectPageSize
                setPageSize={(size) => {
                  // Reset to page 1
                  setPageSize(size);
                  paginationRef?.current?.onReset();
                }}
                type={"second"}
                ref={pageSizeRef}
              />
            </span>
          </div>
        </div>
      </div>
    </>
  );
};
